import * as THREE from "three";

// const texLoader = new THREE.TextureLoader();
const phongShader = THREE.ShaderLib.phong;
const uniformsFoam = THREE.UniformsUtils.clone(phongShader.uniforms);
const uniformsBlank = THREE.UniformsUtils.clone(phongShader.uniforms);

function setPhongUniformValue(
  uniforms: typeof uniformsFoam,
  key: string,
  value: THREE.Color | number,
): void {
  const u = uniforms[key];
  if (u && typeof u === "object" && "value" in u) {
    (u as { value: THREE.Color | number }).value = value;
  }
}

setPhongUniformValue(uniformsFoam, "emissive", new THREE.Color("#222222"));
setPhongUniformValue(
  uniformsFoam,
  "ambientLightColor",
  new THREE.Color("#222222"),
);
setPhongUniformValue(uniformsFoam, "diffuse", new THREE.Color("#222222"));
setPhongUniformValue(uniformsFoam, "specular", new THREE.Color("#222222"));
setPhongUniformValue(uniformsFoam, "shininess", 0.2);
setPhongUniformValue(uniformsFoam, "reflectivity", 0.1);

setPhongUniformValue(uniformsBlank, "emissive", new THREE.Color("#000000"));
setPhongUniformValue(
  uniformsBlank,
  "ambientLightColor",
  new THREE.Color("#FFFFFF"),
);
setPhongUniformValue(uniformsBlank, "diffuse", new THREE.Color("#FFFFFF"));
setPhongUniformValue(uniformsBlank, "specular", new THREE.Color("#FFFFFF"));
setPhongUniformValue(uniformsBlank, "shininess", 1);
setPhongUniformValue(uniformsBlank, "opacity", 0.5);
setPhongUniformValue(uniformsBlank, "reflectivity", 0.3);

const metallic = (color: string): THREE.MeshStandardMaterial =>
  new THREE.MeshStandardMaterial({
    color: new THREE.Color(color),
    metalness: 0.8,
    roughness: 0.2,
    envMapIntensity: 0.8,
    side: THREE.DoubleSide,
  });

const blank = (color: string): THREE.MeshPhongMaterial => {
  const mat = new THREE.MeshPhongMaterial({
    color: new THREE.Color(color),
    //  ambientLightColor: new THREE.Color color
    specular: new THREE.Color("#aaaaaa"),
    shininess: 1,
    //  vertexShader: phongShader.vertexShader
    //  fragmentShader: phongShader.fragmentShader
    side: THREE.DoubleSide,
    // specularMap: texLoader.load("/textures/specmap.jpg", () => { }),
  });

  if (mat.specularMap) {
    const texLocal = mat.specularMap;
    texLocal.wrapS = texLocal.wrapT = THREE.RepeatWrapping;
    texLocal.repeat.set(1 / 100, 1 / 100);
    texLocal.offset.set(-0.27, -0.27);
  }
  return mat;
};

export type MaterialRegistry = Record<
  string,
  THREE.Color | string | THREE.Material
>;

const materialsTable: MaterialRegistry = {
  _whiteOrg: new THREE.Color("#f5f7f7"),
  _whitePlan: new THREE.Color("#c5c7c7"),
  _whiteKey: "weiß (RAL9003)",
  Phenol: blank("#402020"),
  black: blank("#000000"),
  white: blank("#b0b0b0"),
  "PVC rot": blank("#bd151a"),
  "PVC blau": blank("#004585"),
  "PVC silber": blank("#8c979c"),
  "PVC schwarz": blank("#000000"),
  "Poly-Board schwarz": blank("#232324"),
  "schwarz (RAL9004)": blank("#232324"),
  "weiß (RAL9003)": blank("#f5f7f7"),
  "schiefergrau (RAL7015)": blank("#474e52"),
  "grau (RAL7046)": blank("#828a8f"),
  "rot (RAL3020)": blank("#bd151a"),
  "bordeaux (RAL4004)": blank("#691939"),
  "blau (RAL5005)": blank("#004585"),
  "himmelblau (RAL5015)": blank("#0071b3"),
  "nachtblau (RAL5022)": blank("#272757"),
  "navyblau (RAL5013)": blank("#1f304f"),
  "orange (RAL2008)": blank("#f0752e"),
  "gelb (RAL1023)": blank("#f0c800"),
  "pink (RAL4003)": blank("#c4618c"),
  "violett (RAL4005)": blank("#76689a"),
  "minzgrün (RAL6029)": blank("#006f3d"),
  "petrol (RAL5020)": blank("#2a4e57"),
  "olivgrün (RAL6003)": blank("#4c543d"),
  "schokobraun (RAL8017)": blank("#45322b"),
  braun: blank("#2e1d1e"),
  stucco: metallic("#8c979c"),
  carbon: metallic("#404142"),
  "red metallic": metallic("#500000"),
  "blue metallic": metallic("#000050"),
  "orange metallic": metallic("#f0752e"),
  "olive metallic": metallic("#4c543d"),
  volume: metallic("#404142"),
  wood: new THREE.MeshPhongMaterial({
    color: new THREE.Color("#502900"),
    //      ambientLightColor: new THREE.Color "#502900"
    specular: new THREE.Color("#997777"),
    shininess: 0,
    //      vertexShader: phongShader.vertexShader
    //      fragmentShader: phongShader.fragmentShader
    side: THREE.DoubleSide,
    // specularMap: texLoader.load("/textures/spec_plywood.jpg", () => { }),
  }),
  aluminium: new THREE.MeshStandardMaterial({
    color: new THREE.Color("#cdcdcd"),
    metalness: 0.9,
    roughness: 0.15,
    envMapIntensity: 0.9,
    side: THREE.DoubleSide,
  }),
  aluminiumBracket: new THREE.MeshStandardMaterial({
    color: new THREE.Color("#cdcdcd"),
    metalness: 0.9,
    roughness: 0.15,
    envMapIntensity: 0.9,
    side: THREE.DoubleSide,
    // Normal/bump mapping can be added via normalMap and normalScale in StandardMaterial
  }),
  aluminiumBracket2: new THREE.MeshStandardMaterial({
    color: 0xcdcdcd,
    metalness: 0.85,
    roughness: 0.2,
    envMapIntensity: 1.0,
    side: THREE.DoubleSide,
    // Normal/bump mapping can be added via normalMap and normalScale in StandardMaterial
  }),
  foam: new THREE.ShaderMaterial({
    uniforms: uniformsFoam,
    lights: true,
    vertexShader: phongShader.vertexShader,
    fragmentShader: phongShader.fragmentShader,
  }),
  foamTexture: new THREE.MeshBasicMaterial({
    // uniforms: uniformsFoam
    // map: texLoader.load("/images/foam.jpg", () => {}),
  }),
  // vertexShader: phongShader.vertexShader
  // fragmentShader: phongShader.fragmentShader
  blank: new THREE.ShaderMaterial({
    uniforms: uniformsBlank,
    lights: true,
    vertexShader: phongShader.vertexShader,
    fragmentShader: phongShader.fragmentShader,
  }),
  activeSelection: new THREE.LineBasicMaterial({
    color: "red",
  }),
  edges: new THREE.LineBasicMaterial({
    color: 0x777777,
  }),
};

export const Materials: MaterialRegistry = materialsTable;

const volumeMat = Materials.volume;
if (volumeMat instanceof THREE.MeshStandardMaterial) {
  volumeMat.opacity = 0.5;
  volumeMat.transparent = true;
}

// Normal/bump mapping would now be configured via normalMap and normalScale
// in the MeshStandardMaterial definition above
// tex = Materials.wood.specularMap;
// tex.wrapS = (tex.wrapT = THREE.RepeatWrapping);
// tex.repeat.set(1 / 70, 1 / 70);
// tex.offset.set(-0.27, -0.27);
